import type { FieldValues, Path, UseFormReturn } from 'react-hook-form';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { XIcon } from 'lucide-react';

import { DatePicker } from '@/components/ui/date-picker';
import { cn } from '@/lib/utils';

export const DatePickerController = <T extends FieldValues>({
  name,
  methods,
  label,
  wrapperClassName,
  className,
}: {
  label: string;
  name: Path<T>;
  methods: UseFormReturn<T>;
  wrapperClassName?: string;
  className?: string;
}) => {
  const { setError, clearErrors } = useFormContext<T>();
  const { t } = useTranslation();

  return (
    <Controller
      {...methods}
      name={name}
      render={({ field, fieldState: { error } }) => {
        return (
          <div
            className={cn(
              'relative flex w-full flex-col gap-2.5 overflow-hidden',
              wrapperClassName
            )}
          >
            <span
              className={cn('select-none text-xs', !error ? 'text-text-secondary' : 'text-red-500')}
            >
              {label}
            </span>
            <div className="relative">
              <DatePicker
                {...field}
                setIsValid={(isValid) => {
                  if (!isValid) {
                    setError(name, {
                      type: 'manual',
                      message: 'Invalid date',
                    });
                  } else {
                    clearErrors(name);
                  }
                }}
                isValid={!error}
                inputAriaLabel={label}
                value={field.value ? new Date(field.value) : undefined}
                onChange={(date) => {
                  field.onChange(date);
                }}
                className={cn(
                  'flex h-[39px] !w-full !justify-center rounded-xs border bg-primary-lighter px-3 pr-8 shadow-sm transition-colors [--input-color:hsla(var(--primary-color-light))] [--input-focus-color:hsla(var(--primary-color))] [&_input]:w-full [&_input]:text-sm [&_input]:outline-none',
                  !error
                    ? 'border-[--input-color] focus-within:border-[--input-focus-color] hover:border-[--input-focus-color] [&_input]:text-[--input-color]'
                    : 'border-red-500 focus-within:border-red-500 [&_input]:text-red-500',
                  className
                )}
              />
              {field.value ? (
                <button
                  type="button"
                  aria-label={t('clearDate')}
                  className="absolute right-2 top-1/2 z-10 -translate-y-1/2 rounded-full p-1 text-[#768295] hover:bg-gray-100 hover:text-[#2F3A4A]"
                  onClick={(event) => {
                    event.stopPropagation();
                    field.onChange(undefined);
                    clearErrors(name);
                  }}
                >
                  <XIcon aria-hidden className="h-3.5 w-3.5" />
                </button>
              ) : null}
            </div>
          </div>
        );
      }}
    />
  );
};
